import React, { useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { buildCountryOption, buildCountryOptions, createCountryFilter, splitHighlightedText } from "../../utilities/countrySearchUtils";

const HighlightedText = ({ text, query }) => {
	const parts = useMemo(() => splitHighlightedText(text, query), [text, query]);
	return (
		<>
			{parts.map((part, index) => (
				<Box
					component="span"
					key={`${part.text}-${index}`}
					sx={{ fontWeight: part.highlighted ? 700 : 400 }}
				>
					{part.text}
				</Box>
			))}
		</>
	);
};

const CountryAutocomplete = ({
	options = [],
	value = "",
	onChange = () => {},
	label,
	required = false,
	disabled = false,
	error = false,
	helperText = "",
	size = "small",
	margin = "normal",
	fullWidth = true,
	placeholder,
}) => {
	const [inputValue, setInputValue] = useState("");
	const countryOptions = useMemo(() => buildCountryOptions(options), [options]);
	const selectedOption = useMemo(
		() => countryOptions.find((option) => option.label === value) || (value ? buildCountryOption(value) : null),
		[countryOptions, value]
	);

	return (
		<Autocomplete
			options={countryOptions}
			value={selectedOption}
			onInputChange={(_, newInput, reason) => {
				if (reason === "reset") {
					setInputValue("");
					return;
				}
				setInputValue(newInput);
			}}
			onChange={(_, nextValue) => {
				onChange(nextValue?.label || "");
				setInputValue("");
			}}
			filterOptions={createCountryFilter()}
			autoHighlight
			selectOnFocus
			handleHomeEndKeys
			openOnFocus
			clearOnBlur={false}
			disabled={disabled}
			isOptionEqualToValue={(option, selected) => option.label === selected.label}
			getOptionLabel={(option) => option?.label || ""}
			ListboxProps={{ style: { maxHeight: 320 } }}
			renderOption={(props, option) => (
				<Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{option.iso2 ? (
						<img
							loading="lazy"
							width="20"
							src={`https://flagcdn.com/w40/${option.iso2.toLowerCase()}.png`}
							alt=""
						/>
					) : null}
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography variant="body2" noWrap>
							<HighlightedText text={option.label} query={inputValue} />
						</Typography>
					</Box>
					{option.iso2 ? (
						<Typography variant="caption" color="text.secondary">
							<HighlightedText text={option.iso2} query={inputValue} />
						</Typography>
					) : null}
					{option.dialCode ? (
						<Typography variant="caption" color="text.secondary">
							+<HighlightedText text={option.dialCode} query={inputValue} />
						</Typography>
					) : null}
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					disabled={disabled}
					error={error}
					helperText={helperText}
					size={size}
					margin={margin}
					fullWidth={fullWidth}
					placeholder={placeholder}
				/>
			)}
		/>
	);
};

export default CountryAutocomplete;